import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "../api/client";
import { useAppStore } from "../store/appStore";
import DataTable, { Column } from "../components/DataTable";
import { IconSearch } from "../components/Icons";

const LIMIT = 25;

interface Tenant {
  id: string;
  name: string;
  legal_name: string | null;
  company_id: string | null;
  vat_id: string | null;
  email: string | null;
  phone: string | null;
  is_active: boolean;
  plan: string;
  is_vat_payer: boolean;
  email_verified_at: string | null;
  created_at: string;
  last_activity_at: string | null;
  user_count: number;
  device_count: number;
}

export default function Tenants() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const setTenantId = useAppStore((s) => s.setTenantId);
  const setTenant = useAppStore((s) => s.setTenant);

  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState<"all" | "active" | "inactive">("all");
  const [offset, setOffset] = useState(0);

  const { data: tenants, isLoading, error } = useQuery({
    queryKey: ["ops-tenants", q, activeFilter, offset],
    queryFn: async () => {
      const params = new URLSearchParams({ limit: String(LIMIT), offset: String(offset) });
      if (q) params.set("q", q);
      if (activeFilter !== "all") params.set("is_active", activeFilter === "active" ? "true" : "false");
      const res = await api.get<Tenant[]>(`/ops/tenants?${params.toString()}`);
      return res.data;
    },
  });

  const toggleMutation = useMutation({
    mutationFn: async ({ id, is_active }: { id: string; is_active: boolean }) => {
      await api.put(`/ops/tenants/${id}`, { is_active });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["ops-tenants"] }),
  });

  function openTenant(t: Tenant) {
    setTenantId(t.id);
    setTenant({ id: t.id, name: t.name });
    navigate(`/tenants/${t.id}`);
  }

  const columns: Column<Tenant>[] = [
    {
      key: "name",
      header: t("tenants.name"),
      render: (r) => (
        <button className="ghost" onClick={() => openTenant(r)} style={{ justifyContent: "flex-start", padding: 0, fontWeight: 600 }}>
          {r.name}
        </button>
      ),
    },
    { key: "company", header: t("tenants.companyId"), render: (r) => r.company_id ?? "—" },
    { key: "email", header: t("tenants.email"), render: (r) => r.email ?? "—" },
    { key: "plan", header: t("tenants.plan"), render: (r) => r.plan },
    {
      key: "active",
      header: t("common.status"),
      render: (r) => (
        <span className={`badge ${r.is_active ? "badge-green" : "badge-red"}`}>
          {r.is_active ? t("common.active") : t("common.inactive")}
        </span>
      ),
    },
    { key: "counts", header: "Uživ. / Zar.", render: (r) => `${r.user_count} / ${r.device_count}` },
    {
      key: "actions",
      header: "",
      render: (r) => (
        <button
          className={r.is_active ? "danger" : "primary"}
          onClick={() => toggleMutation.mutate({ id: r.id, is_active: !r.is_active })}
          disabled={toggleMutation.isPending}
          style={{ padding: "6px 12px", fontSize: 12 }}
        >
          {r.is_active ? t("tenants.deactivate") : t("tenants.activate")}
        </button>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", gap: 16, flexWrap: "wrap" }}>
        <div>
          <h1>{t("nav.tenants")}</h1>
          <p>{t("tenants.subtitle")}</p>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <div style={{ position: "relative" }}>
            <IconSearch size={18} className="muted" style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)" }} />
            <input
              value={q}
              onChange={(e) => { setQ(e.target.value); setOffset(0); }}
              placeholder={t("tenants.search")}
              style={{ paddingLeft: 40, width: 240 }}
            />
          </div>
          <select
            value={activeFilter}
            onChange={(e) => { setActiveFilter(e.target.value as typeof activeFilter); setOffset(0); }}
            style={{ width: 160 }}
          >
            <option value="all">{t("common.all")}</option>
            <option value="active">{t("tenants.filterActive")}</option>
            <option value="inactive">{t("tenants.filterInactive")}</option>
          </select>
        </div>
      </div>

      <div className="card">
        <DataTable
          rows={tenants ?? []}
          columns={columns}
          loading={isLoading}
          error={error ? t("tenants.loadError") : null}
          emptyMessage={t("tenants.noTenants")}
        />
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", gap: 12, marginTop: 16 }}>
        <button
          onClick={() => setOffset((o) => Math.max(0, o - LIMIT))}
          disabled={offset === 0}
        >
          {t("common.previous")}
        </button>
        <button
          onClick={() => setOffset((o) => o + LIMIT)}
          disabled={(tenants ?? []).length < LIMIT}
        >
          {t("common.next")}
        </button>
      </div>
    </div>
  );
}
